import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MySocketWrapper } from './socket-wrapper';
import { urls } from 'src/app/utils/url.utils';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private registeredListeners = new Map<string, (...args: any[]) => void>();

  constructor(private socket: MySocketWrapper) {
    this.handleSocketLifecycle();
  }

  /** Handle connect/reconnect/disconnect events */
  private handleSocketLifecycle() {
    this.socket.on('connect', () => {
      console.log('%c[Socket Connected]', 'color: green');
      this.rebindListeners();
    });

    this.socket.on('reconnect', (attempt: number) => {
      console.log(`%c[Socket Reconnected] Attempt: ${attempt}`, 'color: lime');
      this.rebindListeners();
    });

    this.socket.on('disconnect', (reason: any) => {
      console.warn('[Socket Disconnected]', reason);
    });
  }

  /** Create observable listener (your usage style) */
  addListener<T = any>(event: string): Observable<T> {
    console.log('Registering listener for event:', event);

    return new Observable<T>(observer => {
      const listener = (data: T) => observer.next(data);

      // Register & track it for reconnects
      this.registerListener(event, listener);

      // Cleanup on unsubscribe
      return () => {
        this.unregisterListener(event);
      };
    });
  }

  /** Register a listener and remember it for reconnects */
  private registerListener(event: string, listener: (...args: any[]) => void) {
    const oldListener = this.registeredListeners.get(event);

    if (oldListener && typeof (this.socket as any).off === 'function') {
      (this.socket as any).off(event, oldListener);
    }

    this.registeredListeners.set(event, listener);
    this.socket.on(event, listener);
  }

  /** Remove listener */
  private unregisterListener(event: string) {
    const listener = this.registeredListeners.get(event);
    if (!listener) return;

    if (typeof (this.socket as any).off === 'function') {
      (this.socket as any).off(event, listener);
    } else if (typeof (this.socket as any).removeListener === 'function') {
      (this.socket as any).removeListener(event, listener);
    }

    this.registeredListeners.delete(event);
  }

  /** Rebind listeners after reconnect */
  private rebindListeners() {
    console.log('Rebinding listeners:', Array.from(this.registeredListeners.keys()));
    for (const [event, listener] of this.registeredListeners.entries()) {
      if (typeof (this.socket as any).off === 'function') {
        (this.socket as any).off(event, listener);
      }
      this.socket.on(event, listener);
    }
  }

  /** Emit event */
  emit(event: string, payload?: any) {
    this.socket.emit(event, payload);
  }

  /** Manually connect socket */
  connectSocket() {
    console.log('[Socket Connect] Attempting...');
    console.log('Socket connected status:', this.socket.ioSocket.connected);
    if (!this.socket.ioSocket.connected) {
      const token = localStorage.getItem('access_token');
      if (token) {
        this.socket.ioSocket.io.opts.extraHeaders = {
          Authorization: `Bearer ${JSON.parse(token)}`
        };
      }
      this.socket.ioSocket.io.uri = urls.SERVER_URL;
      this.socket.connect();
    }
  }

  /** Manual disconnect */
  disconnectSocket() {
    if (this.socket.ioSocket.connected) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.registeredListeners.clear();
    }
  }
}
